/**
 * Valori a mano dell'admin (AUD-F2.20): classe di rischio del fornitore, rating di fornitore e stabilimento; attiva/disattiva
 * fornitore o stabilimento nell'Audit Plan (RAT26-F3.1b). Motivazione obbligatoria.
 */
import { useEffect, useState } from "react";
import { Alert, Button, DatePicker, Input, InputNumber, Modal, Select, Space, Typography } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RISKS } from "@audit-plan/shared";
import { api } from "../../api/client";
import { useFullModal } from "../audit/mobileModal";
import { useNotify } from "../audit/useNotify";
import { Rating } from "../pills";

export type ManualTarget =
  | { kind: "risk"; supplierId: string; name: string; risk: string }
  | { kind: "rating"; entity: "suppliers" | "factories"; id: string; name: string; rating: number | null; manual: boolean }
  | { kind: "active"; entity: "suppliers" | "factories"; id: string; name: string; active: boolean }
  /** phase-out a mano del fornitore (AUD-F8.4), per un fornitore chiuso o da non usare più */
  | { kind: "phaseout"; supplierId: string; name: string; phaseOut: boolean };

export function ManualValueModal({ target, onClose }: { target: ManualTarget | null; onClose: () => void }) {
  const { t } = useTranslation();
  const fm = useFullModal();
  const qc = useQueryClient();
  const n = useNotify();
  const [reason, setReason] = useState("");
  const [risk, setRisk] = useState<string>("medium");
  const [score, setScore] = useState<number | null>(null);
  const [poDate, setPoDate] = useState<dayjs.Dayjs | null>(dayjs());

  useEffect(() => {
    if (!target) return;
    setReason("");
    setPoDate(dayjs());
    if (target.kind === "risk") setRisk(target.risk);
    else if (target.kind === "rating") setScore(target.rating);
  }, [target]);

  const save = useMutation({
    mutationFn: (backToAuto: boolean) => {
      if (!target) return Promise.resolve(null);
      if (target.kind === "risk") return api.put(`/suppliers/${target.supplierId}/risk`, { risk, reason: reason.trim() });
      if (target.kind === "active") return api.put(`/${target.entity}/${target.id}/active`, { active: !target.active, reason: reason.trim() });
      if (target.kind === "phaseout")
        return api.put(`/suppliers/${target.supplierId}/phaseout`, { phaseOut: !target.phaseOut, reason: reason.trim(), ...(!target.phaseOut && poDate ? { date: poDate.format("YYYY-MM-DD") } : {}) });
      return api.put(`/${target.entity}/${target.id}/rating`, { rating: backToAuto ? null : score, reason: reason.trim() });
    },
    onSuccess: () => {
      for (const k of ["supplier", "suppliers", "factory", "factories", "plan", "log", "dashboard", "ncs", "actions", "nc", "action"]) void qc.invalidateQueries({ queryKey: [k] });
      n.ok(t("m_saved"));
      onClose();
    },
    onError: n.fail,
  });

  if (!target) return null;
  const okReason = reason.trim().length >= 3;
  if (target.kind === "active") {
    return (
      <Modal
        {...fm}
        open
        onCancel={onClose}
        title={`${target.active ? t("act_titleOff") : t("act_titleOn")} · ${target.name}`}
        okText={t("confirm")}
        cancelText={t("cancel")}
        okButtonProps={{ disabled: !okReason, danger: target.active }}
        confirmLoading={save.isPending}
        onOk={() => save.mutate(false)}
        destroyOnHidden
      >
        <Alert type="info" showIcon style={{ marginBottom: 12 }} message={target.active ? t("act_hintOff") : t("act_hintOn")} />
        <Typography.Text>{t("m_reason")} *</Typography.Text>
        <Input.TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("m_reasonPh")} maxLength={1000} />
      </Modal>
    );
  }
  if (target.kind === "phaseout") {
    const set = !target.phaseOut;
    return (
      <Modal
        {...fm}
        open
        onCancel={onClose}
        title={`${set ? t("po_setTitle") : t("po_unsetTitle")} · ${target.name}`}
        okText={t("confirm")}
        cancelText={t("cancel")}
        okButtonProps={{ disabled: !okReason || (set && !poDate), danger: set }}
        confirmLoading={save.isPending}
        onOk={() => save.mutate(false)}
        destroyOnHidden
      >
        <Alert type={set ? "warning" : "info"} showIcon style={{ marginBottom: 12 }} message={set ? t("po_setHint") : t("po_unsetHint")} />
        {set && (
          <div style={{ marginBottom: 12 }}>
            <Typography.Text>{t("po_date")}</Typography.Text>
            <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" value={poDate} onChange={setPoDate} />
          </div>
        )}
        <Typography.Text>{t("po_reason")} *</Typography.Text>
        <Input.TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("m_reasonPh")} maxLength={1000} />
      </Modal>
    );
  }
  const isRisk = target.kind === "risk";
  return (
    <Modal
      {...fm}
      open
      onCancel={onClose}
      title={`${isRisk ? t("m_setRisk") : t("m_setRating")} · ${target.name}`}
      okText={t("confirm")}
      cancelText={t("cancel")}
      okButtonProps={{ disabled: !okReason || (!isRisk && score == null) }}
      confirmLoading={save.isPending}
      onOk={() => save.mutate(false)}
      destroyOnHidden
    >
      <Alert type="info" showIcon style={{ marginBottom: 12 }} message={isRisk ? t("m_riskHint") : t("m_ratingHint")} />
      {isRisk ? (
        <div style={{ marginBottom: 12 }}>
          <Typography.Text>{t("risk")}</Typography.Text>
          <Select style={{ width: "100%" }} value={risk} onChange={setRisk} options={RISKS.map((r) => ({ value: r, label: t(`risk_${r}`) }))} />
        </div>
      ) : (
        <div style={{ marginBottom: 12 }}>
          <Typography.Text>{t("m_score")}</Typography.Text>
          <Space style={{ display: "flex" }} align="center">
            <InputNumber min={0} max={100} step={0.5} value={score} onChange={(v) => setScore(v == null ? null : Number(v))} style={{ width: 140 }} />
            <span style={{ opacity: 0.65 }}>{t("m_class")}</span>
            <Rating score={score} />
          </Space>
        </div>
      )}
      <Typography.Text>{t("m_reason")} *</Typography.Text>
      <Input.TextArea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={t("m_reasonPh")} maxLength={1000} />
      {!isRisk && target.manual && (
        <div style={{ marginTop: 12 }}>
          <Button disabled={!okReason} loading={save.isPending} onClick={() => save.mutate(true)}>{t("m_backToAuto")}</Button>
        </div>
      )}
    </Modal>
  );
}
